import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UtensilsCrossed, Loader2 } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { cuisineLabel, deriveDiscovery } from "@/utils/cuisines";
import { CuisineModal } from "@/components/CuisineModal";

/* Carte « Cuisine & spécialités » du tableau de bord restaurant :
   types de cuisine (1-3), spécialités, catégories de découverte auto-déduites. */
export default function CuisineEditorCard() {
  const { t, i18n } = useTranslation();
  const [cuisines, setCuisines] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/my/restaurant").then(({ data }) => {
      setCuisines(data.cuisines || []);
      setSpecialties(data.specialties || []);
    }).catch(() => setCuisines([]));
  }, []);

  const toggleCuisine = (v) => setCuisines((c) =>
    c.includes(v) ? c.filter((x) => x !== v) : c.length >= 3 ? c : [...c, v]);
  const toggleSpecialty = (v) => setSpecialties((s) =>
    s.includes(v) ? s.filter((x) => x !== v) : [...s, v]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.patch("/my/restaurant/settings", { cuisines, specialties });
      setCuisines(data.cuisines || cuisines);
      setSpecialties(data.specialties || specialties);
      setOpen(false);
      toast.success(t("rdash.cuisineSaved"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (cuisines === null) return null;
  const discovery = deriveDiscovery(cuisines, specialties);
  return (
    <div className="bg-white border border-sand rounded-2xl p-5" data-testid="cuisine-editor-card">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2">
          <UtensilsCrossed className="w-4 h-4 text-brand-red" /> {t("rdash.cuisineTitle")}
        </h3>
        <button onClick={() => setOpen(true)} data-testid="cuisine-edit-btn"
          className="rounded-full border border-sand hover:border-ink px-4 py-2 text-sm font-semibold">
          {t("rdash.cuisineEdit")}
        </button>
      </div>
      <div className="text-[11px] uppercase tracking-widest text-mute mb-1.5">{t("rdash.cuisineTypes")}</div>
      <div className="flex flex-wrap gap-2">
        {cuisines.length === 0 && <span className="text-sm text-mute">{t("rdash.cuisineNone")}</span>}
        {cuisines.map((v) => (
          <span key={v} className="rounded-full bg-ink text-white px-3 py-1.5 text-sm font-semibold" data-testid={`cuisine-tag-${v}`}>
            {cuisineLabel(v, i18n.language)}
          </span>
        ))}
      </div>
      <div className="text-[11px] uppercase tracking-widest text-mute mt-3 mb-1.5">{t("rdash.specialtiesTitle")}</div>
      <div className="flex flex-wrap gap-2">
        {specialties.length === 0 && <span className="text-sm text-mute">{t("rdash.specialtiesNone")}</span>}
        {specialties.map((v) => (
          <span key={v} className="rounded-full bg-elevated px-3 py-1.5 text-sm font-semibold" data-testid={`specialty-tag-${v}`}>
            {cuisineLabel(v, i18n.language)}
          </span>
        ))}
      </div>
      {discovery.length > 0 && (
        <>
          <div className="text-[11px] uppercase tracking-widest text-mute mt-3 mb-1.5">{t("rdash.discoveryTitle")}</div>
          <div className="flex flex-wrap gap-2" data-testid="discovery-tags">
            {discovery.map((v) => (
              <span key={v} className="rounded-full border border-sand px-3 py-1.5 text-xs text-mute" data-testid={`discovery-tag-${v}`}>
                {cuisineLabel(v, i18n.language)}
              </span>
            ))}
          </div>
          <p className="text-[11px] text-mute mt-1.5">{t("rdash.discoveryHint")}</p>
        </>
      )}
      <CuisineModal open={open} onClose={() => setOpen(false)}
        cuisines={cuisines} specialties={specialties}
        onToggleCuisine={toggleCuisine} onToggleSpecialty={toggleSpecialty}
        onDone={save} />
      <div className="mt-3">
        <button onClick={save} disabled={saving || cuisines.length === 0} data-testid="cuisine-save-btn"
          className="inline-flex items-center gap-2 rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} {t("rdash.cuisineSave")}
        </button>
      </div>
    </div>
  );
}
